import {
  TaskManagementNoteTypeCreateDocument,
  TaskManagementNoteTypeDeleteDocument,
  TaskManagementNoteTypeGetByIdDocument,
  TaskManagementNoteTypeListDocument,
  TaskManagementNoteTypeListPaginatedDocument,
  TaskManagementNoteTypeUpdateDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { ResourceEnum, ScopeEnum } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  ICreateNoteTypeVars,
  IGetNoteTypeByIdReturnType,
  IGetNotesListArgs,
  INoteTypeModel,
  INotesListReturnType,
  IUpdateNoteTypeVars,
} from "api/services/library/note-type";

export async function getTaskManagementNoteTypesList(
  variables: IGetNotesListArgs
): Promise<INotesListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementNoteTypeListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.taskManagementNoteTypeListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createTaskManagementNoteType(
  variables: ICreateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementNoteTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all note type queries so they refetch with the new item
    executeEviction(client, {
      typename: "TaskManagementNoteType",
      fieldNames: [
        "taskManagementNoteTypeListPaginated",
        "taskManagementNoteTypeList",
      ],
    });

    return res?.data?.taskManagementNoteTypeCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateTaskManagementNoteType(
  variables: IUpdateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementNoteTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "TaskManagementNoteType",
      id: variables.noteTypeId.toString(),
      fieldNames: [
        "taskManagementNoteTypeListPaginated",
        "taskManagementNoteTypeList",
        "taskManagementNoteTypeGetById",
      ],
    });

    return res?.data?.taskManagementNoteTypeUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteTaskManagementNoteType(
  noteTypeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementNoteTypeDeleteDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "TaskManagementNoteType",
      id: noteTypeId.toString(),
      fieldNames: [
        "taskManagementNoteTypeListPaginated",
        "taskManagementNoteTypeList",
        "taskManagementNoteTypeGetById",
      ],
    });

    return Boolean(res?.data?.taskManagementNoteTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementNoteType(
  noteTypeId: number
): Promise<IGetNoteTypeByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementNoteTypeGetByIdDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.taskManagementNoteTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementNoteTypes(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<INoteTypeModel, "noteTypeId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementNoteTypeListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.taskManagementNoteTypeList ?? [];
  } catch (error) {
    throw error;
  }
}
